/**
 * El cronómetro que dibujan las pantallas.
 *
 * Vive aquí y no en la SPA porque el proyector y los cuarenta móviles tienen que dibujar **el mismo
 * número**: dos implementaciones divergirían, y CE-002 —medio segundo de diferencia como mucho—
 * dejaría de cumplirse sin que nadie lo notara hasta tener la clase delante.
 *
 * **El servidor sigue siendo la única autoridad del tiempo** (principio I). Esto no decide nada:
 * dibuja. La decisión de cuándo se cierra una pregunta la toma el servidor con su reloj, y una
 * respuesta que llegue tarde se rechaza por mucho que el cronómetro del alumno dijera otra cosa.
 */
import { MAX_LATENCY_COMPENSATION_MS } from '../scoring.js';
/**
 * Convierte lo que llega del servidor en un anclaje.
 *
 * `monoNow` es `performance.now()` **en el momento de recibir**, y `rttMs` el último ida y vuelta
 * medido. Se descuenta la mitad del ida y vuelta porque el mensaje tardó eso en bajar: sin ese
 * descuento, el cronómetro del alumno va retrasado exactamente lo que tardó en llegar, y eso no es
 * un error simétrico sino un sesgo —llega a cero **después** de que el servidor haya cerrado, así
 * que el alumno pulsa viendo tiempo en pantalla y recibe un rechazo—.
 *
 * Devuelve nulo cuando no hay nada que dibujar: reloj congelado u hora ilegible. **Nulo no es
 * cero**: una cuenta atrás agotada y una partida en pausa se dibujan distinto.
 */
export function anchorFrom(timer, monoNow, rttMs) {
    if (timer.endsAt === null)
        return null;
    const endsAt = Date.parse(timer.endsAt);
    const serverNow = Date.parse(timer.serverNow);
    if (Number.isNaN(endsAt) || Number.isNaN(serverNow))
        return null;
    const travel = Math.min(rttMs / 2, MAX_LATENCY_COMPENSATION_MS);
    const remainingMs = Math.max(0, endsAt - serverNow - travel);
    return { remainingMs, monoAtReceipt: monoNow };
}
/**
 * Lo que queda ahora mismo, según el reloj monotónico.
 *
 * Devuelve nulo si no hay anclaje, para que quien dibuja distinga «parado» de «se acabó».
 */
export function remainingAt(anchor, monoNow) {
    if (anchor === null)
        return null;
    const elapsed = Math.max(0, monoNow - anchor.monoAtReceipt);
    return Math.max(0, anchor.remainingMs - elapsed);
}
/** A partir de aquí, la discrepancia entre los dos relojes ya no es imprecisión. */
export const SUSPENSION_THRESHOLD_MS = 2000;
/**
 * ¿Ha estado el dispositivo suspendido, o le han cambiado la hora?
 *
 * Es el reverso de haber elegido un reloj monotónico: no lo afecta cambiar la hora —que es lo que se
 * quería—, pero **tampoco cuenta el tiempo suspendido**. Un Android que se bloquea a mitad de
 * pregunta vuelve creyendo que no ha pasado nada y seguiría dibujando el cronómetro donde lo dejó.
 *
 * La hora del sistema se usa **solo aquí**, como delator, nunca como fuente: si los dos relojes
 * discrepan, lo que procede es esperar al siguiente anclaje del servidor, no fiarse de ninguno.
 */
export function suspicionOfSuspension(monoDeltaMs, wallDeltaMs) {
    return Math.abs(wallDeltaMs - monoDeltaMs) > SUSPENSION_THRESHOLD_MS;
}
//# sourceMappingURL=clock.js.map